import React from 'react';
import Styled from 'styled';
import { useQuery } from '@apollo/client/react/hooks';
import { toGlobalId, fromGlobalId } from 'graphql-relay';
import classnames from 'classnames';

import FieldWithLabelView from 'four-nets/ui/form/field-with-label-view';

import query, {
    CategoryPropertiesValuesQuery,
    CategoryPropertiesValuesQueryVariables,
} from './category-properties-values.graphql';
import defaultStyles from './category-properties-values.scss';

export interface Props {
    styles: typeof defaultStyles;
    categoryId: string;
    onCompleted: (data: CategoryPropertiesValuesQuery) => void;
    onClick: (property: string, value: string) => void;
    selected: Set<string>;
}

type Values = NonNullable<
    NonNullable<NonNullable<CategoryPropertiesValuesQuery['categoryPropertyValues']>['propertiesValues']>[0]
>['values'];

const ColorValues: React.FC<{
    property: string | null;
    values: Values;
    styles: typeof defaultStyles;
    onClick: (property: string, value: string) => void;
    selected: Set<string>;
}> = props => {
    const { property, values, styles, onClick, selected } = props;

    if (!values || !property) {
        return null;
    }

    const quotient = Math.floor(values.length / 3);
    const remainder = values.length % 3;

    const part1 = values.slice(0, quotient + remainder);
    const part2 = values.slice(quotient + remainder, 2 * quotient + remainder);
    const part3 = values.slice(2 * quotient + remainder);

    return (
        <div className={styles.colors}>
            <div className={styles.column}>
                {part1.map(value => (
                    <button
                        type='button'
                        className={classnames(styles.value, {
                            [styles.selected]: selected.has(fromGlobalId(value!.id!).id),
                        })}
                        key={value!.id}
                        onClick={() => {
                            onClick(property, fromGlobalId(value!.id!).id);
                        }}
                    >
                        <div
                            style={{
                                background: value!.background || '',
                            }}
                            className={styles.color}
                        />
                        <div className={styles.colorLabel}>{value!.value}</div>
                    </button>
                ))}
            </div>
            <div className={styles.column}>
                {part2.map(value => (
                    <button
                        type='button'
                        className={classnames(styles.value, {
                            [styles.selected]: selected.has(fromGlobalId(value!.id!).id),
                        })}
                        key={value!.id}
                        onClick={() => {
                            onClick(property, fromGlobalId(value!.id!).id);
                        }}
                    >
                        <div
                            style={{
                                background: value!.background || '',
                            }}
                            className={styles.color}
                        />
                        <div className={styles.colorLabel}>{value!.value}</div>
                    </button>
                ))}
            </div>
            <div className={styles.column}>
                {part3.map(value => (
                    <button
                        type='button'
                        className={classnames(styles.value, {
                            [styles.selected]: selected.has(fromGlobalId(value!.id!).id),
                        })}
                        key={value!.id}
                        onClick={() => {
                            onClick(property, fromGlobalId(value!.id!).id);
                        }}
                    >
                        <div
                            style={{
                                background: value!.background || '',
                            }}
                            className={styles.color}
                        />
                        <div className={styles.colorLabel}>{value!.value}</div>
                    </button>
                ))}
            </div>
        </div>
    );
};

const CategoryPropertiesValue: React.FC<Props> = React.memo(props => {
    const { styles, categoryId, onClick, onCompleted, selected } = props;

    const { data } = useQuery<CategoryPropertiesValuesQuery, CategoryPropertiesValuesQueryVariables>(query, {
        variables: { categoryId: toGlobalId('ServerCategoryNode', categoryId) },
        onCompleted,
    });

    return data && data.categoryPropertyValues && data.categoryPropertyValues.propertiesValues ? (
        <>
            {data.categoryPropertyValues.propertiesValues.map(item =>
                item ? (
                    <FieldWithLabelView
                        id={item.id}
                        key={item.id}
                        field={
                            item.propertyGettext === 'COLOR' ? (
                                <ColorValues
                                    styles={styles}
                                    values={item.values}
                                    property={item.property}
                                    selected={selected}
                                    onClick={onClick}
                                />
                            ) : (
                                <div>
                                    {item.values!.map(value => (
                                        <button
                                            type='button'
                                            style={{
                                                background: value!.background || '',
                                            }}
                                            className={classnames(styles.value, {
                                                [styles.selected]: selected.has(fromGlobalId(value!.id!).id),
                                            })}
                                            key={value!.id}
                                            onClick={() => {
                                                onClick(item.property!, fromGlobalId(value!.id!).id);
                                            }}
                                        >
                                            {value!.value}
                                        </button>
                                    ))}
                                </div>
                            )
                        }
                        label={item.property}
                    />
                ) : null
            )}
        </>
    ) : null;
});

export default Styled(defaultStyles)(CategoryPropertiesValue);
